import React, { useState, useEffect } from 'react';
import { Typography, Box, Button, Paper, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, TablePagination, CircularProgress, IconButton, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../../services/api';
import { toast } from 'react-toastify';

const AssociationsList = () => {
  const navigate = useNavigate();
  const [associations, setAssociations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch associations on component mount
  useEffect(() => {
    fetchAssociations();
  }, []);

  const fetchAssociations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/associations');
      setAssociations(response.data);
    } catch (error) {
      console.error('Error fetching associations:', error);
      toast.error('Failed to load associations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddAssociation = () => {
    navigate('/admin/associations/new');
  };

  const handleEditAssociation = (id) => {
    navigate(`/admin/associations/${id}/edit`);
  };

  const handleDeleteAssociation = async (id) => {
    if (window.confirm('Are you sure you want to deactivate this association?')) {
      try {
        await api.delete(`/api/admin/associations/${id}`);
        toast.success('Association deactivated successfully');
        fetchAssociations(); // Refresh the list
      } catch (error) {
        console.error('Error deactivating association:', error);
        toast.error('Failed to deactivate association');
      }
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Associations</Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={handleAddAssociation}
        >
          Add Association
        </Button>
      </Box>
      
      {loading ? (
        <Box display="flex" justifyContent="center" my={5}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Industry</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Expiry Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {associations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">No associations found</TableCell>
                  </TableRow>
                ) : (
                  associations
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((association) => (
                      <TableRow key={association.id}>
                        <TableCell>{association.name}</TableCell>
                        <TableCell>{association.industry || '-'}</TableCell>
                        <TableCell>{association.city || '-'}</TableCell>
                        <TableCell>
                          <Chip 
                            label={association.is_active ? 'Active' : 'Inactive'} 
                            color={association.is_active ? 'success' : 'error'} 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell>
                          {association.expiry_date ? new Date(association.expiry_date).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell>
                          <IconButton 
                            size="small" 
                            color="primary" 
                            onClick={() => handleEditAssociation(association.id)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="error" 
                            onClick={() => handleDeleteAssociation(association.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={associations.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}
    </Box>
  );
};

export default AssociationsList;